// P-SILENT-FAILURE-BATCH (QA-AUDIT-2026-06-01 Area 1 P1) — pure boot-time
// session resolver, split out of useAuth.js so the catch-the-rejection path
// is unit-testable WITHOUT React or a live Supabase client.
//
// THE BUG IT CLOSES: useAuth used to call
//   supabase.auth.getSession().then(({ data: { session } }) => { … })
// with NO error handling. getSession can fail two ways:
//   (a) the promise REJECTS (network down / SDK throw) — the .then never
//       runs, so setLoading(false) never fires → the app hangs on the
//       <Loading/> spinner forever with no recovery, and
//   (b) it RESOLVES with a non-null { error } envelope (auth error returned
//       as a value rather than thrown) — the old code ignored `error`.
// Either way the parent saw an indefinite spinner and no action.
//
// resolveInitialAuthUser NEVER throws: it folds BOTH failure modes into a
// returned { user, error }. The hook then clears its loading state and, on
// error, surfaces it (Sentry + toast) and falls through to the login screen
// so the parent has something to act on. Transparency, not silence.

/** MA-779 — how long boot waits for getSession before giving up. */
export const AUTH_BOOT_TIMEOUT_MS = 10_000;

/** MA-779 — the error resolveInitialAuthUser returns when getSession did not
 *  settle within AUTH_BOOT_TIMEOUT_MS. */
export class AuthBootTimeoutError extends Error {
  constructor(ms) {
    super(`getSession did not settle within ${ms} ms`);
    this.name = "AuthBootTimeoutError";
  }
}

/**
 * Resolve the boot-time auth user from a Supabase-shaped client. Never
 * rejects — on any failure it returns { user: null, error } so the caller
 * can always clear its loading state.
 *
 * MA-779 — ⚠️AND NEVER WAITS FOREVER. In a browser auth-js serialises every
 * auth call on one navigator lock. When the token refresh cannot succeed
 * (the device is offline, or — since MA-779 softens it — the auth server
 * answers the refresh with a 5xx, a 429 or a gateway 401), auth-js keeps the
 * session and keeps retrying, holding that lock through each ~25 s backoff
 * round and taking it again on every 30 s auto-refresh tick. getSession()
 * queues behind it for as long as the outage lasts, so boot sat on the
 * Loading splash indefinitely (measured: 4+ minutes, no toast). This races
 * getSession against `timeoutMs`; on timeout it returns
 * `{ user: null, error: AuthBootTimeoutError }`, so useAuth takes its
 * existing failure path (Sentry, the "We couldn't check your sign-in" toast,
 * the sign-in screen). The STORED SESSION IS NOT TOUCHED: when the refresh
 * later succeeds, auth-js emits TOKEN_REFRESHED and useAuth's
 * onAuthStateChange signs the parent back in without a new sign-in.
 *
 * @param {{ auth: { getSession: () => Promise<{ data?: { session?: { user?: unknown } | null } | null, error?: unknown }> } }} client
 * @param {{ timeoutMs?: number }} [options]
 * @returns {Promise<{ user: unknown, error: unknown }>}
 */
export async function resolveInitialAuthUser(client, { timeoutMs = AUTH_BOOT_TIMEOUT_MS } = {}) {
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve({ user: null, error: new AuthBootTimeoutError(timeoutMs) }), timeoutMs);
  });
  const settle = (async () => {
    try {
      const { data, error } = await client.auth.getSession();
      if (error) return { user: null, error };
      return { user: data?.session?.user ?? null, error: null };
    } catch (error) {
      // A thrown/rejected getSession (network error, SDK fault) — fold it into
      // the same shape so the caller treats it identically to an error envelope.
      return { user: null, error };
    }
  })();
  try {
    return await Promise.race([settle, timeout]);
  } finally {
    clearTimeout(timer);
  }
}
